'use client';

import { Button, cn, NumberField, Radio, RadioGroup } from '@heroui/react';
import { IconPlayerPause, IconPlayerPlay, IconRefresh } from '@tabler/icons-react';
import type { FC } from 'react';
import { useSyncExternalStore } from 'react';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import type { TimerBlock } from '@/components/canvas/blocks/types';
import {
  clampDurationField,
  DEFAULT_TIMER_DURATION,
  DEFAULT_TIMER_MODE,
  dispatchTimerCommand,
  getTimerControlView,
  getTimerSessionVersion,
  readTimerSession,
  subscribeTimerSession,
  type TimerDuration,
  type TimerDurationField,
  type TimerMode,
} from '@/components/Timer';

const ICON_STROKE = 1.25;

const DURATION_FIELDS: { key: TimerDurationField; label: string; max: number }[] = [
  { key: 'hours', label: '时', max: 99 },
  { key: 'minutes', label: '分', max: 59 },
  { key: 'seconds', label: '秒', max: 59 },
];

const ROW_LABEL_CLASS =
  'w-10 shrink-0 whitespace-nowrap text-sm font-medium text-default-foreground';

export interface TimerPropertiesProps {
  block: TimerBlock;
  className?: string;
}

export const TimerProperties: FC<TimerPropertiesProps> = ({ block, className }) => {
  const { updateBlockData } = useCanvas();
  const { data } = block;
  const target = data.defaultTarget ?? DEFAULT_TIMER_DURATION;
  const mode = data.mode ?? DEFAULT_TIMER_MODE;
  useSyncExternalStore(
    (onStoreChange) => subscribeTimerSession(block.id, onStoreChange),
    () => getTimerSessionVersion(block.id),
  );
  const session = readTimerSession(block.id);
  const control = getTimerControlView(
    target,
    session?.status ?? 'idle',
    session?.elapsedMs ?? 0,
  );

  const PlayPauseIcon = control.playPauseIcon === 'pause' ? IconPlayerPause : IconPlayerPlay;

  const patchTarget = (patch: Partial<TimerDuration>) => {
    const next = { ...target, ...patch };
    const changed =
      next.hours !== target.hours ||
      next.minutes !== target.minutes ||
      next.seconds !== target.seconds;
    if (!changed) return;

    const status = readTimerSession(block.id)?.status ?? 'idle';
    updateBlockData(block.id, { defaultTarget: next });

    if (status === 'running' || status === 'paused' || status === 'completed') {
      dispatchTimerCommand(block.id, 'reset');
    }
  };

  const patchMode = (next: TimerMode) => {
    if (next === mode) return;
    updateBlockData(block.id, { mode: next });
    const status = readTimerSession(block.id)?.status ?? 'idle';
    if (status === 'running' || status === 'paused' || status === 'completed') {
      dispatchTimerCommand(block.id, 'reset');
    }
  };

  return (
    <div className={cn('property-panel-view flex min-h-0 w-full shrink-0 flex-col gap-4 p-1', className)}>
      <p className="text-sm font-semibold text-default-foreground">定时设置</p>

      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <span className={ROW_LABEL_CLASS}>模式</span>
          <RadioGroup
            value={mode}
            onChange={(value) => patchMode(value as TimerMode)}
            orientation="horizontal"
            name={`timer-mode-${block.id}`}
            className="min-w-0 flex-1 flex flex-wrap gap-x-4 gap-y-1"
            variant="secondary"
          >
            <Radio value="countDown">
              <Radio.Control>
                <Radio.Indicator />
              </Radio.Control>
              <Radio.Content>
                <span className="whitespace-nowrap text-sm text-default-foreground">倒计时</span>
              </Radio.Content>
            </Radio>
            <Radio value="countUp">
              <Radio.Control>
                <Radio.Indicator />
              </Radio.Control>
              <Radio.Content>
                <span className="whitespace-nowrap text-sm text-default-foreground">正计时</span>
              </Radio.Content>
            </Radio>
          </RadioGroup>
        </div>

        {DURATION_FIELDS.map(({ key, label, max }) => (
          <div key={key} className="flex items-center gap-3">
            <span className={ROW_LABEL_CLASS}>{label}</span>
            <NumberField
              aria-label={`${label}数`}
              variant="secondary"
              className="min-w-0 flex-1"
              value={target[key]}
              minValue={0}
              maxValue={max}
              step={1}
              onChange={(v) => {
                if (v == null || Number.isNaN(v)) return;
                patchTarget({ [key]: clampDurationField(key, v) });
              }}
            >
              <NumberField.Group className="min-w-0 flex-1">
                <NumberField.DecrementButton />
                <NumberField.Input />
                <NumberField.IncrementButton />
              </NumberField.Group>
            </NumberField>
          </div>
        ))}
      </div>

      <div className="flex gap-3 pt-1">
        <Button
          type="button"
          variant="primary"
          className="h-10 flex-1 rounded-full bg-red-500 text-white data-[hover=true]:bg-red-600"
          isDisabled={control.playPauseDisabled}
          onPress={() => dispatchTimerCommand(block.id, 'togglePlayPause')}
        >
          <span className="inline-flex items-center justify-center gap-1.5">
            <PlayPauseIcon className="size-4 shrink-0" stroke={ICON_STROKE} />
            {control.playPauseLabel}
          </span>
        </Button>
        <Button
          type="button"
          variant="secondary"
          className="h-10 flex-1 rounded-full border border-red-500 bg-background text-default-foreground data-[hover=true]:bg-default-100"
          isDisabled={control.resetDisabled}
          onPress={() => dispatchTimerCommand(block.id, 'reset')}
        >
          <span className="inline-flex items-center justify-center gap-1.5">
            <IconRefresh className="size-4 shrink-0" stroke={ICON_STROKE} />
            重置
          </span>
        </Button>
      </div>
    </div>
  );
};
